import { useMemo } from 'react';
import type { MatchPublic, PredictionPublic } from '@mondy/shared';
import { MatchCard } from '@/components/MatchCard';
import { dayKey, formatDayHeading } from '@/lib/format';

function groupByDay(matches: MatchPublic[]): {
  key: string;
  heading: string;
  items: MatchPublic[];
}[] {
  const buckets = new Map<string, MatchPublic[]>();
  for (const m of matches) {
    const k = dayKey(m.kickoffUtc);
    const bucket = buckets.get(k);
    if (bucket) bucket.push(m);
    else buckets.set(k, [m]);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, items]) => ({
      key: k,
      heading: items[0] ? formatDayHeading(items[0].kickoffUtc) : k,
      items,
    }));
}

interface Props {
  matches: MatchPublic[];
  predictionsByMatch?: Map<string, PredictionPublic>;
  /** Match id to tag with `scrollAnchorId` so callers can scroll it into view. */
  scrollTargetMatchId?: string;
  /** DOM id applied to the card matching `scrollTargetMatchId`. */
  scrollAnchorId?: string;
}

/**
 * Matches grouped by kickoff day, each rendered as a clickable MatchCard that
 * links to the match detail page. Shared by the schedule and team-detail pages.
 */
export function MatchDayList({
  matches,
  predictionsByMatch,
  scrollTargetMatchId,
  scrollAnchorId,
}: Props) {
  const grouped = useMemo(() => groupByDay(matches), [matches]);

  return (
    <div className="matches">
      {grouped.map((day) => (
        <div key={day.key}>
          <div className="day-head">
            <span className="d">{day.heading.split(',')[0]}</span>
            <span className="date-sep">·</span>
            <span>{day.heading.split(',')[1]?.trim() ?? ''}</span>
            <span className="rule" />
            <span>
              {day.items.length} match{day.items.length === 1 ? '' : 'es'}
            </span>
          </div>
          {day.items.map((m) => (
            <MatchCard
              key={m.id}
              m={m}
              prediction={predictionsByMatch?.get(m.id)}
              id={m.id === scrollTargetMatchId ? scrollAnchorId : undefined}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
